import React, { useState } from 'react';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { Card } from '../cards/Card';
import { Button } from '../buttons/Button';
import { Badge } from './Badge';
import { Trash2, MessageSquare, Plus } from 'lucide-react';

interface InternalNotesProps {
    targetId: string;
    targetType: 'property' | 'user' | 'landlord' | 'booking';
}

export const InternalNotes: React.FC<InternalNotesProps> = ({ targetId, targetType }) => {
    const { internalNotes, addNote, deleteNote, users } = useData();
    const { user } = useAuth();
    const [newNote, setNewNote] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const notes = internalNotes.filter(n => n.targetId === targetId && n.targetType === targetType && !n.deletedAt);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newNote.trim()) return;

        setIsSubmitting(true);
        await addNote({
            userId: user?.id || '',
            targetId,
            targetType,
            content: newNote.trim()
        });
        setNewNote('');
        setIsSubmitting(false);
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="w-4 h-4 text-slate-400" />
                <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Internal Notes</h4>
                <Badge color="blue" className="text-[10px]">{notes.length}</Badge>
            </div>

            <Card className="p-4 bg-slate-50 border-slate-100">
                <form onSubmit={handleSubmit} className="space-y-3">
                    <textarea
                        className="w-full border border-gray-200 rounded-lg p-3 text-sm focus:ring-2 focus:ring-[#2f5c96] outline-none h-20 bg-white"
                        placeholder="Add a private note for staff..."
                        value={newNote}
                        onChange={e => setNewNote(e.target.value)}
                    />
                    <div className="flex justify-end">
                        <Button size="sm" type="submit" disabled={isSubmitting || !newNote.trim()}>
                            <Plus className="w-4 h-4 mr-1" /> Add Note
                        </Button>
                    </div>
                </form>

                <div className="mt-4 space-y-3">
                    {notes.map(note => {
                        const author = users.find(u => u.id === note.userId);
                        return (
                            <div key={note.id} className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm group">
                                <div className="flex justify-between items-start mb-2">
                                    <div className="flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-500">
                                            {author?.name?.charAt(0) || 'U'}
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold text-gray-900">{author?.name || 'Staff member'}</p>
                                            <p className="text-[10px] text-gray-400">{new Date(note.createdAt).toLocaleString()}</p>
                                        </div>
                                    </div>
                                    {(user?.role === 'ADMIN' || user?.id === note.userId) && (
                                        <button
                                            onClick={() => deleteNote(note.id)}
                                            className="text-gray-300 hover:text-red-500 transition-colors hidden group-hover:block"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{note.content}</p>
                            </div>
                        );
                    })}
                    {notes.length === 0 && (
                        <p className="text-center text-xs text-gray-400 py-4 italic">No internal notes yet.</p>
                    )}
                </div>
            </Card>
        </div>
    );
};
